import { useMemo, useState } from "react";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { normalizeForCompare } from "@/lib/normalizeText";
import type { Term } from "../types";

interface RelatedTermsPickerProps {
  /** Full candidate list to choose from (typically the loaded catalog). */
  allTerms: Term[];
  /** Currently selected related-term UUIDs, in display order. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** The term being edited — excluded so a term can't relate to itself. */
  excludeId?: string;
}

const MAX_RESULTS = 50;

/** Only approved + visible terms can show on the public term page. */
function isPubliclyVisible(term: Term): boolean {
  return term.status === "approved" && term.is_enabled;
}

export function RelatedTermsPicker({
  allTerms,
  value,
  onChange,
  excludeId,
}: RelatedTermsPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const byId = useMemo(() => {
    const map = new Map<string, Term>();
    for (const t of allTerms) {
      if (t.id) {
        map.set(t.id, t);
      }
    }
    return map;
  }, [allTerms]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const candidates = useMemo(() => {
    const q = normalizeForCompare(query);
    // Only offer terms that can actually appear publicly (approved + visible).
    const pool = allTerms.filter(
      t => t.id && t.id !== excludeId && isPubliclyVisible(t)
    );
    const matches = q
      ? pool.filter(
          t =>
            normalizeForCompare(t.word).includes(q) ||
            normalizeForCompare(t.word_gujarati ?? "").includes(q) ||
            normalizeForCompare(t.word_devanagari ?? "").includes(q) ||
            normalizeForCompare(t.word_hindi ?? "").includes(q)
        )
      : pool;
    return matches.slice(0, MAX_RESULTS);
  }, [allTerms, query, excludeId]);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter(v => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const remove = (id: string) => onChange(value.filter(v => v !== id));

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map(id => {
            const term = byId.get(id);
            // A picked term that isn't approved+visible will be dropped publicly.
            const hidden = !term || !isPubliclyVisible(term);
            return (
              <Badge
                key={id}
                variant="secondary"
                className={cn(
                  "gap-1 py-1 pl-2.5 pr-1 font-normal",
                  hidden &&
                    "border-amber-300 bg-amber-50 text-amber-800"
                )}
                title={
                  hidden
                    ? "Not approved/visible — won't appear on the public page"
                    : undefined
                }
              >
                <span className="max-w-[180px] truncate">
                  {term?.word ?? id}
                </span>
                {hidden && <span className="text-amber-600">· hidden</span>}
                <button
                  type="button"
                  onClick={() => remove(id)}
                  className="hover:bg-muted-foreground/20 rounded-sm p-0.5"
                  aria-label={`Remove ${term?.word ?? id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(o => !o)}
        className="text-muted-foreground w-full justify-between font-normal"
      >
        <span className="flex items-center">
          <Plus className="mr-2 h-4 w-4" />
          {value.length > 0
            ? "Add another related term…"
            : "Add related terms…"}
        </span>
        <ChevronDown
          className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
        />
      </Button>

      {open && (
        <div className="rounded-md border">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search terms…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              <CommandEmpty>
                {allTerms.length === 0
                  ? "Loading terms…"
                  : "No matching terms."}
              </CommandEmpty>
              <CommandGroup>
                {candidates.map(term => {
                  const checked = selectedSet.has(term.id);
                  return (
                    <CommandItem
                      key={term.id}
                      value={term.id}
                      onSelect={() => toggle(term.id)}
                      className="gap-2"
                    >
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          checked ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{term.word}</span>
                        {(term.word_devanagari || term.word_gujarati) && (
                          <span className="text-muted-foreground font-devanagari block truncate text-xs">
                            {[term.word_devanagari, term.word_gujarati]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        )}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
